import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PageBusyLoader } from '../ui/PageBusyLoader';
import {
  BriefcaseFill,
  CalendarEventFill,
  CheckCircleFill,
  EyeFill,
  MortarboardFill,
} from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import { fetchUserProgress, type ProfileCompletionBreakdownRow } from '../Preview/api';
import {
  countCompletedProfileSections,
  profileCompletionMessage,
  resolveScreeningReadiness,
  screeningPromptProgress,
} from '../../utils/profileCompletionScore';
import { useSessionStore } from '../../store/sessionStore';
import { useDashboardRealtimeRefresh } from '../../hooks/useDashboardRealtimeRefresh';
import { ReturnedReference } from '../Create-Portfolio/types';
import {
  getReferenceFeedbackSummary,
  parseReferenceVerificationAnswers,
} from '../../utils/referenceFeedback';
import {
  fetchCandidateDashboardMetrics,
  fetchDashboardLearning,
  trackLearningClick,
  type CandidateDashboardMetrics,
  type LearningCard,
} from './dashboardApi';
import { CurrentTrends } from './CurrentTrends';
import { RecentActivityFeed } from './RecentActivityFeed';
import { ExternalProfileActivityFeed } from './ExternalProfileActivityFeed';
import { ScreeningReadinessModal } from './ScreeningReadinessModal';
import { ProfileAccessRequestModal } from './ProfileAccessRequestModal';
import { getProfileInitials } from '../../utils/profileInitials';
import './Dashboard.css';

type DashboardState = {
  loading: boolean;
  userData: any;
  promptCount: number;
  promptVideosCount: number;
  hasPortfolioContent: boolean;
  jobTitle: string;
  readinessPercent: number;
  progressBreakdown: { step: string; done: boolean; weight: number }[];
  profileSections: ProfileCompletionBreakdownRow[];
  profileSuggestions: string[];
};

/** API may omit or vary `kind`; map everything to slim row styles consistently. */
function normalizeLearningKind(item: LearningCard): 'webinar' | 'certificate' {
  const raw = String(item.kind ?? '').toLowerCase();
  if (raw === 'certificate' || raw === 'cert') return 'certificate';
  if (raw === 'webinar' || raw === 'seminar' || raw === 'course') {
    return 'webinar';
  }
  const href = (item.ctaHref || '').toLowerCase();
  if (href.includes('certificate')) return 'certificate';
  if (href.includes('webinar')) return 'webinar';
  const title = (item.title || '').toLowerCase();
  const cta = (item.ctaLabel || '').toLowerCase();
  if (
    /\bcert(ificate|ified)?\b/.test(title) ||
    title.includes('cse') ||
    cta.includes('program') ||
    cta.includes('certificate')
  ) {
    return 'certificate';
  }
  return 'webinar';
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const sessionPrompts = useSessionStore((state) => state.prompts);
  const sessionPortfolio = useSessionStore((state) => state.portfolio);
  const [metrics, setMetrics] = useState<CandidateDashboardMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [learning, setLearning] = useState<LearningCard[]>([]);
  const [showReadinessModal, setShowReadinessModal] = useState(false);
  const [references, setReferences] = useState<ReturnedReference[]>([]);
  const [profileImgFailed, setProfileImgFailed] = useState(false);

  const [state, setState] = useState<DashboardState>({
    loading: true,
    userData: null,
    promptCount: 0,
    promptVideosCount: 0,
    hasPortfolioContent: false,
    jobTitle: '',
    readinessPercent: 0,
    progressBreakdown: [],
    profileSections: [],
    profileSuggestions: [],
  });

  useEffect(() => {
    let isMounted = true;
    let loaded = false;

    const loadDashboard = async () => {
      if (loaded) return;
      try {
        const sessionStore = useSessionStore.getState();
        const {
          user: userData,
          prompts: promptsData,
          portfolio: portfolioData,
        } = await sessionStore.bootstrapSession();
        await sessionStore.refreshUser({ force: true });
        const progressData = await fetchUserProgress();

        if (!isMounted) return;
        loaded = true;

        const user = userData as {
          profileImg?: string;
          firstName?: string;
          lastName?: string;
          username?: string;
          jobTitle?: string;
          company?: string;
        } | null;
        const prompts = Array.isArray(promptsData) ? promptsData : [];
        const firstPromptJobTitle = (prompts[0] as { jobTitle?: string } | undefined)?.jobTitle;
        const promptVideosCount = prompts.filter(
          (prompt) =>
            (prompt as { fileUrl?: string })?.fileUrl &&
            (prompt as { fileUrl?: string }).fileUrl !== 'N/A'
        ).length;

        const portfolio = portfolioData as {
          overview?: string;
          projects?: unknown[];
          references?: unknown[];
          certifications?: unknown[];
          accomplishments?: unknown[];
          organizations?: unknown[];
        } | null;
        const hasPortfolioContent = Boolean(
          portfolio &&
            (portfolio.overview ||
              portfolio.projects?.length ||
              portfolio.references?.length ||
              portfolio.certifications?.length ||
              portfolio.accomplishments?.length ||
              portfolio.organizations?.length)
        );

        // Critical-path render: show dashboard as soon as session payload is ready.
        // Metrics + learning are loaded in the background to avoid blocking first paint.
        setReferences(
          Array.isArray(portfolio?.references) ? (portfolio!.references as ReturnedReference[]) : []
        );

        setState({
          loading: false,
          userData: user || null,
          promptCount: prompts.length,
          promptVideosCount,
          hasPortfolioContent,
          jobTitle: firstPromptJobTitle || '',
          readinessPercent: progressData?.percent ?? 0,
          progressBreakdown: progressData?.breakdown || [],
          profileSections: progressData?.sections || [],
          profileSuggestions: progressData?.suggestions || [],
        });

        void Promise.allSettled([fetchCandidateDashboardMetrics(), fetchDashboardLearning()]).then(
          ([metricsResult, learningResult]) => {
            if (!isMounted) return;
            setMetricsLoading(false);
            if (metricsResult.status === 'fulfilled') {
              setMetrics(metricsResult.value);
            }
            if (learningResult.status === 'fulfilled') {
              setLearning(learningResult.value);
            }
          }
        );
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        if (isMounted) {
          setState((prev) => ({ ...prev, loading: false }));
          setMetricsLoading(false);
        }
      }
    };

    loadDashboard();
    return () => {
      isMounted = false;
    };
  }, []);

  const refreshDashboardWidgets = useCallback(async () => {
    try {
      await useSessionStore.getState().refreshUser({ force: true });
      const [metricsResult, learningResult, progressData, portfolioData] = await Promise.allSettled(
        [
          fetchCandidateDashboardMetrics(),
          fetchDashboardLearning(),
          fetchUserProgress(),
          useSessionStore.getState().refreshPortfolio({ force: true }),
        ]
      );

      if (metricsResult.status === 'fulfilled') {
        setMetrics(metricsResult.value);
      }
      if (learningResult.status === 'fulfilled') {
        setLearning(learningResult.value);
      }
      if (progressData.status === 'fulfilled' && progressData.value) {
        setState((prev) => ({
          ...prev,
          userData: useSessionStore.getState().currentUser ?? prev.userData,
          readinessPercent: progressData.value.percent ?? prev.readinessPercent,
          progressBreakdown: progressData.value.breakdown || prev.progressBreakdown,
          profileSections: progressData.value.sections || prev.profileSections,
          profileSuggestions: progressData.value.suggestions || prev.profileSuggestions,
        }));
      }
      if (portfolioData.status === 'fulfilled') {
        const portfolio = portfolioData.value as {
          references?: unknown[];
        } | null;
        const refs = portfolio?.references;
        if (Array.isArray(refs)) {
          setReferences(refs as ReturnedReference[]);
        }
      }
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  useDashboardRealtimeRefresh(refreshDashboardWidgets);

  useEffect(() => {
    const syncProgress = () => {
      void (async () => {
        await useSessionStore.getState().refreshUser({ force: true });
        const progressData = await fetchUserProgress();
        setState((prev) => ({
          ...prev,
          readinessPercent: progressData.percent,
          userData: useSessionStore.getState().currentUser ?? prev.userData,
          progressBreakdown: progressData.breakdown,
          profileSections: progressData.sections,
          profileSuggestions: progressData.suggestions,
        }));
      })();
    };
    window.addEventListener('prompts-updated', syncProgress);
    window.addEventListener('portfolio-updated', syncProgress);
    window.addEventListener('profile-updated', syncProgress);
    return () => {
      window.removeEventListener('prompts-updated', syncProgress);
      window.removeEventListener('portfolio-updated', syncProgress);
      window.removeEventListener('profile-updated', syncProgress);
    };
  }, []);

  const displayName = useMemo(() => {
    if (state.userData?.firstName || state.userData?.lastName) {
      return `${state.userData?.firstName || ''} ${state.userData?.lastName || ''}`.trim();
    }
    return state.userData?.username || 'there';
  }, [state.userData]);

  const firstName = useMemo(() => {
    if (state.userData?.firstName) return state.userData.firstName;
    return displayName.split(' ')[0] || 'there';
  }, [displayName, state.userData?.firstName]);

  const profileInitials = useMemo(() => getProfileInitials(state.userData), [state.userData]);

  useEffect(() => {
    setProfileImgFailed(false);
  }, [state.userData?.profileImg]);

  const roleTitle = useMemo(() => {
    const base =
      state.jobTitle ||
      state.userData?.jobTitle ||
      state.userData?.username ||
      state.userData?.company;

    return (base || 'Candidate').toString().toUpperCase();
  }, [state.jobTitle, state.userData]);

  const readinessPercent = useMemo(
    () => Math.max(0, Math.min(100, Math.round(state.readinessPercent || 0))),
    [state.readinessPercent]
  );

  const sectionCounts = useMemo(
    () => countCompletedProfileSections(state.profileSections),
    [state.profileSections]
  );
  const completedProfileSections = sectionCounts.completed;
  const totalProfileSections = sectionCounts.total;
  const promptProgress = useMemo(() => screeningPromptProgress(sessionPrompts), [sessionPrompts]);

  const readinessLabel =
    readinessPercent >= 85
      ? 'Ready to Skip Screening'
      : readinessPercent >= 60
        ? 'Almost Ready for Screening'
        : 'Building Screening Readiness';
  const readinessLoading = state.loading;

  const readyBannerTitle =
    readinessPercent >= 85
      ? 'You’re Ready!'
      : readinessPercent >= 60
        ? 'Almost There'
        : 'Let’s Build Your Profile';

  const readyBannerCopy = useMemo(() => {
    if (readinessPercent >= 100) {
      return 'Your profile looks great. Sit tight and recruiters will be in touch soon.';
    }
    return profileCompletionMessage(
      resolveScreeningReadiness(state.userData, sessionPrompts, sessionPortfolio)
    );
  }, [readinessPercent, state.userData, sessionPrompts, sessionPortfolio]);

  const profileViews = metrics?.profileViews ?? 0;
  const profileViewsBadge = metrics?.profileViewsBadge ?? profileViews;
  const interestedRecruiters = metrics?.interestedRecruiters ?? 0;
  if (state.loading) {
    return <PageBusyLoader pageName="Dashboard" />;
  }

  return (
    <div className="v1-dashboard-page">
      <main className="v1-dashboard-main">
        <section className="v1-hero">
          <div className="v1-hero-copy">
            <h1 className="v1-hero-title">Your Profile is Live!</h1>
            <p className="v1-hero-sub">
              Great job! Recruiters can now <strong>view your profile</strong> and invite you to
              interview.
            </p>
          </div>
        </section>

        <div className="v1-dash-body">
          <div className="v1-dash-columns">
            <div className="v1-dash-col-left">
              <section className="v1-card v1-card-profile">
                <h2 className="v1-card-heading">Welcome {firstName}!</h2>
                <div className="v1-profile-block">
                  <div className="v1-avatar-xl">
                    {state.userData?.profileImg && !profileImgFailed ? (
                      <img
                        src={state.userData.profileImg}
                        alt={displayName}
                        onError={() => setProfileImgFailed(true)}
                      />
                    ) : (
                      <span className="v1-avatar-fallback" aria-hidden="true">
                        {profileInitials}
                      </span>
                    )}
                  </div>
                  <h3 className="v1-profile-name">{displayName}</h3>
                  <div className="v1-role-pill">{roleTitle}</div>
                  <div className="v1-profile-actions">
                    <button
                      type="button"
                      className="v1-btn v1-btn-primary v1-btn-block"
                      onClick={() => navigate('/user-profile')}
                    >
                      View My Profile
                    </button>
                    <button
                      type="button"
                      className="v1-btn v1-btn-outline v1-btn-block"
                      onClick={() => {
                        if (state.userData?.username) {
                          navigate(`/u/${state.userData.username}?preview=1`);
                        }
                      }}
                      disabled={!state.userData?.username}
                    >
                      Preview Recruiter View
                    </button>
                  </div>
                </div>
                <div className="v1-ready-banner">
                  <div className="v1-ready-title">
                    <CheckCircleFill className="v1-inline-icon" /> {readyBannerTitle}
                  </div>
                  <p className="v1-ready-copy">{readyBannerCopy}</p>
                  <p className="v1-ready-meta">
                    {completedProfileSections}/{totalProfileSections} profile sections complete
                  </p>
                </div>
              </section>

              {references.length > 0 ? (
                <section className="v1-card v1-dash-references-preview v1-dash-trends-span">
                  <div className="v1-card-head v1-card-head-between">
                    <h3 className="v1-card-heading v1-card-heading-sm">Reference Feedback</h3>
                    <button
                      type="button"
                      className="v1-link-btn v1-link-btn-inline"
                      onClick={() => navigate('/dashboard/references')}
                    >
                      View All ›
                    </button>
                  </div>
                  <div className="v1-ref-list">
                    {references.slice(0, 2).map((ref) => {
                      const answers = parseReferenceVerificationAnswers(ref.verificationAnswers);
                      const summary = getReferenceFeedbackSummary(answers);
                      return (
                        <button
                          key={ref.id}
                          type="button"
                          className="v1-ref-item v1-ref-item-clickable"
                          onClick={() =>
                            navigate(`/dashboard/references/${ref.id}`, {
                              state: { from: '/dashboard' },
                            })
                          }
                        >
                          <div className="v1-ref-item-top">
                            <div>
                              <div className="v1-ref-name">{ref.name}</div>
                              <div className="v1-ref-role">
                                {ref.role}
                                {ref.organization ? ` · ${ref.organization}` : ''}
                              </div>
                            </div>
                            <span
                              className={`v1-ref-status ${
                                ref.isReferenceConfirmed
                                  ? 'v1-ref-status-verified'
                                  : 'v1-ref-status-pending'
                              }`}
                            >
                              {ref.isReferenceConfirmed ? 'Verified' : 'Pending'}
                            </span>
                          </div>
                          {ref.isReferenceConfirmed && summary ? (
                            <p className="v1-ref-summary v1-ref-summary-clamp">{summary}</p>
                          ) : (
                            <p className="v1-card-muted v1-ref-pending-note">
                              Waiting for {ref.name} to complete verification.
                            </p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </section>
              ) : null}

              <CurrentTrends />
            </div>

            <div className="v1-dash-col-right">
              <section className="v1-card v1-dash-dashboard-cell">
                <h2 className="v1-card-heading">Your Dashboard</h2>
                <div className="v1-stats-row">
                  <div className="v1-stat">
                    <div className="v1-stat-top">
                      <div className="v1-stat-left">
                        <EyeFill className="v1-stat-ico" />
                        <div className="v1-stat-lbl">Profile Views</div>
                      </div>
                      <div className="v1-stat-num">
                        {metricsLoading ? (
                          <span className="v1-inline-spinner" aria-label="Loading" />
                        ) : (
                          profileViews
                        )}
                      </div>
                    </div>
                    <div className="v1-stat-hint">
                      {metricsLoading
                        ? 'Loading...'
                        : `${profileViewsBadge} unique recruiter${
                            profileViewsBadge === 1 ? '' : 's'
                          } viewed`}
                    </div>
                  </div>
                  <div className="v1-stat">
                    <div className="v1-stat-top">
                      <div className="v1-stat-left">
                        <BriefcaseFill className="v1-stat-ico" />
                        <div className="v1-stat-lbl">Interested Recruiters</div>
                      </div>
                      <div className="v1-stat-num">
                        {metricsLoading ? (
                          <span className="v1-inline-spinner" aria-label="Loading" />
                        ) : (
                          interestedRecruiters
                        )}
                      </div>
                    </div>
                    <div className="v1-stat-hint">
                      {metricsLoading
                        ? 'Loading...'
                        : interestedRecruiters === 1
                          ? 'Recruiter who viewed your public profile'
                          : 'Recruiters who viewed your public profile'}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="v1-btn v1-btn-primary v1-btn-block v1-btn-icon"
                  onClick={() => navigate('/calendar')}
                >
                  <CalendarEventFill className="v1-btn-lead-icon" />
                  Manage Availability
                </button>
                <button
                  type="button"
                  className="v1-btn v1-btn-outline v1-btn-block mt-2"
                  onClick={() => navigate('/dashboard/negotiator-center')}
                >
                  Open Negotiation Center
                </button>
              </section>

              <div className="v1-dash-mid-row">
                <section className="v1-card v1-card-readiness-compact v1-dash-screening-cell">
                  <div className="v1-card-head v1-card-head-readiness">
                    <CheckCircleFill className="v1-card-head-ico" />
                    <h3 className="v1-card-heading v1-card-heading-sm">Screening Readiness</h3>
                  </div>
                  <div className="v1-readiness v1-readiness-tight">
                    <div className="v1-readiness-top">
                      <span className="v1-readiness-pct">
                        {readinessLoading ? (
                          <span className="v1-inline-spinner" aria-label="Loading" />
                        ) : (
                          `${readinessPercent}%`
                        )}
                      </span>
                      <span className="v1-readiness-lbl">
                        {readinessLoading ? 'Loading...' : readinessLabel}
                      </span>
                    </div>
                    <div className="v1-progress-track">
                      <progress
                        className="v1-progress-native"
                        max={100}
                        value={readinessLoading ? 0 : readinessPercent}
                      />
                    </div>
                    <button
                      type="button"
                      className="v1-link-btn"
                      onClick={() => setShowReadinessModal(true)}
                    >
                      See Details <span aria-hidden>›</span>
                    </button>
                    <div className="v1-readiness-meta">
                      {readinessLoading ? (
                        'Loading...'
                      ) : (
                        <>
                          {completedProfileSections}/{totalProfileSections} sections complete
                          {promptProgress.slotsRemaining > 0 ? (
                            <>
                              {' '}
                              · {promptProgress.count}/{promptProgress.target} screening prompts
                            </>
                          ) : null}
                        </>
                      )}
                    </div>
                  </div>
                </section>

                <div className="v1-dash-learning-stack">
                  <section className="v1-card v1-card-min-h v1-dash-learning-cell v1-dash-learning-cell--in-stack">
                    <div className="v1-card-head">
                      <MortarboardFill className="v1-card-head-ico" />
                      <div>
                        <h3 className="v1-card-heading v1-card-heading-sm">Continued Learning</h3>
                        <p className="v1-card-subhead">
                          Seminars & Certificates to Grow in Your Role
                        </p>
                      </div>
                    </div>
                    <div className="v1-learn-stack">
                      {learning.length === 0 ? (
                        <div className="v1-card-muted">
                          No learning recommendations yet. We are still finding the best seminars,
                          webinars, and certifications for your role.
                        </div>
                      ) : (
                        learning.map((item, learnIndex) => {
                          const kind = normalizeLearningKind(item);
                          return (
                            <a
                              key={`learn-${learnIndex}-${kind}-${item.title}`}
                              href={item.ctaHref || '#'}
                              target="_blank"
                              rel="noreferrer"
                              className={`v1-learn-slim ${
                                kind === 'certificate'
                                  ? 'v1-learn-slim-cert'
                                  : 'v1-learn-slim-webinar'
                              }`}
                              onClick={() => {
                                if (item.id) {
                                  trackLearningClick(item.id);
                                }
                              }}
                            >
                              <div className="v1-learn-slim-top">
                                {item.badgeLabel ? (
                                  <span
                                    className={`v1-tag v1-tag-slim ${
                                      kind === 'certificate' ? 'v1-tag-purple' : 'v1-tag-green'
                                    }`}
                                  >
                                    {item.badgeLabel.toUpperCase()}
                                  </span>
                                ) : kind === 'certificate' ? (
                                  <span className="v1-tag v1-tag-slim v1-tag-purple">
                                    CERTIFICATE
                                  </span>
                                ) : (
                                  <span className="v1-tag v1-tag-slim v1-tag-green">WEBINAR</span>
                                )}
                                {item.timeBadge ? (
                                  <span className="v1-learn-slim-badge">{item.timeBadge}</span>
                                ) : null}
                              </div>
                              <div className="v1-learn-slim-row">
                                <span className="v1-learn-slim-title-wrap">
                                  <span className="v1-learn-slim-title">{item.title}</span>
                                </span>
                                <span className="v1-learn-slim-chevron" aria-hidden>
                                  ↗
                                </span>
                              </div>
                            </a>
                          );
                        })
                      )}
                    </div>
                  </section>
                  <RecentActivityFeed />
                  <ExternalProfileActivityFeed />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <ScreeningReadinessModal
        show={showReadinessModal}
        onHide={() => setShowReadinessModal(false)}
        readinessPercent={readinessPercent}
        readinessLabel={readinessLabel}
        sections={state.profileSections}
        suggestions={state.profileSuggestions}
        promptCount={promptProgress.count}
      />
      <ProfileAccessRequestModal />
    </div>
  );
};

export default Dashboard;
